import { translate, Translation } from '@teamleader/i18n';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import PaymentMethods from './PaymentMethods';
import { useIsExtraLarge, useIsHuge, useIsSmall } from '../../../../../Components/Context/Hooks/mediaQueries';
import useCurrencyFormatter from '../../../../../Components/Context/Hooks/useCurrencyFormatter';
import useLogoPosition from '../../../../../Components/Context/Hooks/useLogoPosition';
import Box from '../../../../../Components/UI/Box';
import Branding from '../../../../../Components/UI/Branding';
import Link, { SIZES as LINK_SIZES } from '../../../../../Components/UI/Link';
import MobilePopup from '../../../../../Components/UI/MobilePopup';
import OverflowingFlexBox from '../../../../../Components/UI/OverflowingFlexBox';
import Sidebar, { SIZES } from '../../../../../Components/UI/Sidebar';
import Document from '../../../../../Svg/120x120_document.svg';
import { CreatePayment, PaymentMethodsResponse } from '../../../../../Types/api';
import { PaymentMethod } from '../../../../../Types/payment';
import { MENUS } from '../../../../../Utils/Menus';
import { EVENTS, track } from '../../../../../Utils/analytics';
import { canOpenNavigationForInvoice } from '../../../../../Utils/canOpenNavigation';
import { getStatusCode } from '../../../../../Utils/errors';
import { HTTP_STATUS_CODES } from '../../../../../Utils/errors/NetworkError';
import { getInvoiceName } from '../../../../../Utils/invoiceName';
import { captureException } from '../../../../../Utils/sentry';
import useGlobalContext, { InvoiceContext } from '../../../Context';
import { SEARCH_PARAMS } from '../../../Home';
import ErrorMessage from '../../ErrorMessage';
import SuccessMessage from '../../SuccessMessage';
import styles from '../../styles.css';

enum PAYMENT_STATE {
  IDLE = 'idle',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  FAILURE = 'failure',
  ALREADY_COMPLETE = 'alreadyComplete',
}

const PaymentMethodMenu: React.FC = () => {
  const { handleCloseMenu, request, type } = useGlobalContext();
  const isSmall = useIsSmall();
  const isExtraLarge = useIsExtraLarge();
  const isHuge = useIsHuge();
  const [paymentState, setPaymentState] = useState(PAYMENT_STATE.IDLE);
  const [isDisclaimerExpanded, setIsDisclaimerExpanded] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>();
  const [searchParams, setSearchParams] = useSearchParams();
  const Element = isSmall ? MobilePopup : Sidebar;
  const {
    invoice,
    invoiceId,
    openInvoices,
    paidInvoices,
    creditedInvoices,
    setMadeTooManyPaymentAttempts,
    clearCache,
  } = useContext(InvoiceContext);
  const formatter = useCurrencyFormatter(invoice?.total.payable.currency);
  const { inSuccessMessage } = useLogoPosition(
    canOpenNavigationForInvoice(openInvoices?.data, paidInvoices?.data, creditedInvoices?.data),
  );

  const paymentMethodParams = useMemo<[string, RequestInit]>(
    () => ['/checkout.listPaymentMethods', { body: JSON.stringify({ invoice_id: invoiceId }) }],
    [invoiceId],
  );
  const { data, error } = useSWR<PaymentMethodsResponse>(paymentMethodParams);

  const paymentMethods = data?.data;
  const isTooManyAttemptsError = getStatusCode(error) === HTTP_STATUS_CODES.TOO_MANY_REQUESTS;

  useEffect(() => {
    if (isTooManyAttemptsError) {
      setMadeTooManyPaymentAttempts!(true);
    }
  }, [isTooManyAttemptsError, setMadeTooManyPaymentAttempts]);

  useEffect(() => {
    if (paymentState === PAYMENT_STATE.IDLE && invoice && invoice.total.due.amount === 0) {
      setPaymentState(PAYMENT_STATE.ALREADY_COMPLETE);
      void clearCache!();
    }
  }, [clearCache, invoice, paymentState]);

  const handleSubmit = useCallback(
    (method: PaymentMethod) => {
      (async () => {
        setSelectedPaymentMethod(method);
        setPaymentState(PAYMENT_STATE.PROCESSING);

        let paymentResult;
        try {
          paymentResult = await request!<CreatePayment>('/checkout.createPayment', {
            body: JSON.stringify({
              invoice_id: invoice!.id,
              payment_method: method.id,
              description: getInvoiceName(invoice!.invoiceNumber, invoice!.client.language),
            }),
          });
        } catch (error) {
          setSelectedPaymentMethod(undefined);
          setPaymentState(PAYMENT_STATE.FAILURE);
          captureException(error);
          return;
        }

        setPaymentState(PAYMENT_STATE.SUCCESS);

        // Offline payment methods don't really have a 'finish' action, which means the user is more likely to
        // go back using the browser back button. In these cases we want to make sure the payment status menu is
        // shown rather than the payment method menu.
        if (method.isOfflinePaymentMethod) {
          searchParams.set(SEARCH_PARAMS.OPEN_MENU, MENUS.PAYMENT_STATUS);
          setSearchParams(searchParams, { replace: true });
        }

        window.location.href = paymentResult.data.checkoutUrl;
        track(EVENTS.SELECTED_PAYMENT_METHOD);
      })();
    },
    [invoice, request, searchParams, setSearchParams],
  );

  const handleClosePaymentMenu = useCallback(() => {
    if (paymentState === PAYMENT_STATE.IDLE) {
      track(EVENTS.CANCELLED_SELECTING_PAYMENT_METHOD);
    }
    handleCloseMenu!();
  }, [handleCloseMenu, paymentState]);

  const handleExpandDisclaimer = useCallback(() => {
    track(EVENTS.CLICKED_TERMS_OF_SERVICE);
    setIsDisclaimerExpanded(true);
  }, []);

  const handleCollapseDisclaimer = useCallback(() => {
    setIsDisclaimerExpanded(false);
  }, []);

  if (!invoice) {
    return null;
  }

  const isProcessing = paymentState === PAYMENT_STATE.PROCESSING || paymentState === PAYMENT_STATE.SUCCESS;
  const hasError = paymentMethods?.length === 0 || error;
  return (
    <Element
      className={styles['menu']}
      display="flex"
      flexDirection="column"
      size={isHuge ? SIZES.WIDEST : isExtraLarge ? SIZES.WIDER : undefined}
      onClose={!isProcessing ? handleClosePaymentMenu : undefined}
    >
      {paymentState === PAYMENT_STATE.ALREADY_COMPLETE && (
        <SuccessMessage
          icon={<Document />}
          title={translate('paymentSidebar.alreadyCompleteMessageTitle')}
          description={translate('paymentSidebar.alreadyCompleteMessageText')}
          branding={
            inSuccessMessage ? (
              <Branding language={invoice.client.language} country={invoice.client.country} type={type} />
            ) : undefined
          }
        />
      )}
      {paymentState !== PAYMENT_STATE.ALREADY_COMPLETE && (
        <>
          {hasError ? (
            <ErrorMessage
              icon={<Document />}
              title={
                isTooManyAttemptsError
                  ? translate('paymentSidebar.tooManyAttemptsMessageTitle')
                  : translate('paymentSidebar.errorMessageTitle')
              }
              description={
                isTooManyAttemptsError
                  ? translate('paymentSidebar.tooManyAttemptsMessageText')
                  : translate('paymentSidebar.errorMessageText')
              }
              allowRetry={false}
            />
          ) : (
            <>
              <OverflowingFlexBox>
                <Box paddingHorizontal={4} className={styles['container']}>
                  <Box marginBottom={2}>
                    <h1 className={styles['heading-1']}>{translate('paymentSidebar.title')}</h1>
                  </Box>
                  <Box marginBottom={4}>
                    <h2 className={styles['heading-2']}>{invoice.invoiceNumber}</h2>
                  </Box>
                  <Box className={styles['description']} marginBottom={3}>
                    <Translation
                      id="paymentSidebar.description"
                      values={{
                        amount: (): React.ReactNode => (
                          <span className={styles['description-bold']}>
                            {formatter.format(invoice.total.due.amount)}
                          </span>
                        ),
                        department: (): React.ReactNode => (
                          <span className={styles['description-bold']}>{invoice.department.name}</span>
                        ),
                      }}
                    />
                  </Box>
                  <PaymentMethods
                    onClick={handleSubmit}
                    paymentMethods={paymentMethods}
                    selectedPaymentMethod={selectedPaymentMethod}
                  />
                  {!isDisclaimerExpanded && (
                    <Box
                      marginTop={2}
                      className={styles['disclaimer']}
                      onClick={isSmall ? handleExpandDisclaimer : undefined}
                    >
                      {translate('paymentSidebar.disclaimer.short')}
                      <Link size={LINK_SIZES.SMALL} muted marginLeft={1} onClick={handleExpandDisclaimer}>
                        {translate('actions.readMore')}
                      </Link>
                    </Box>
                  )}
                  {isDisclaimerExpanded && (
                    <Box
                      marginTop={2}
                      className={styles['disclaimer']}
                      onClick={isSmall ? handleCollapseDisclaimer : undefined}
                    >
                      {translate('paymentSidebar.disclaimer')}
                      <Link size={LINK_SIZES.SMALL} muted display="block" onClick={handleCollapseDisclaimer}>
                        {translate('actions.readLess')}
                      </Link>
                    </Box>
                  )}
                </Box>
              </OverflowingFlexBox>
            </>
          )}
        </>
      )}
    </Element>
  );
};

export default PaymentMethodMenu;
